import React, { useState } from 'react';
import { I } from '../../../../common/v5/config';
import './DebugErrorView.css';

const DebugErrorView = ({
  errorData = {},
  nodeName = '',
  debugMode = false
}) => {
  const [copied, setCopied] = useState(false);

  const getErrorMessage = () => {
    if (errorData.error) {
      return errorData.error;
    }
    if (errorData.message) {
      return errorData.message;
    }
    if (errorData.status === 'error') {
      return I('Tool test failed with an unknown error');
    }
    return I('An error occurred during tool testing');
  };

  const copyErrorDetails = async () => {
    const errorInfo = {
      nodeName,
      error: getErrorMessage(),
      ...errorData,
      timestamp: new Date().toISOString()
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(errorInfo, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy error details:', err);
    }
  };

  return (
    <div className="debug-error-container">
      <div className="debug-error-header">
        <div>
          <h4 className="debug-error-title">
            <i className="fa fa-exclamation-triangle" />
            {I('Tool Test Failed')} {nodeName && `- ${nodeName}`}
          </h4>
          <p className="debug-error-message">{getErrorMessage()}</p>
        </div>
        <button className="debug-error-copy-button" onClick={copyErrorDetails}>
          <i className={`fa ${copied ? 'fa-check' : 'fa-copy'}`} />
          {copied ? I('Copied!') : I('Copy')}
        </button>
      </div>

      {debugMode && (
        <>
          {/* Basic Error Details */}
          {(errorData.status_code || errorData.response_time_ms || errorData.test_method) && (
            <details className="debug-error-details-section">
              <summary className="debug-error-details-summary">
                <i className="fa fa-chevron-right debug-error-chevron-icon" />
                {I('Test Details')}
              </summary>
              <div className="debug-error-details-content">
                {errorData.status_code && (
                  <div className="debug-error-detail-row">
                    <p className="debug-error-detail-label">{I('Status Code')}</p>
                    <div className="debug-error-detail-value">{errorData.status_code}</div>
                  </div>
                )}
                {errorData.response_time_ms !== undefined && (
                  <div className="debug-error-detail-row">
                    <p className="debug-error-detail-label">{I('Response Time')}</p>
                    <div className="debug-error-detail-value">{errorData.response_time_ms}ms</div>
                  </div>
                )}
                {errorData.test_method && (
                  <div className="debug-error-detail-row">
                    <p className="debug-error-detail-label">{I('Test Method')}</p>
                    <div className="debug-error-detail-value">{errorData.test_method}</div>
                  </div>
                )}
                {errorData.url && (
                  <div className="debug-error-detail-row">
                    <p className="debug-error-detail-label">{I('URL')}</p>
                    <div className="debug-error-detail-value">{errorData.url}</div>
                  </div>
                )}
              </div>
            </details>
          )}

          {/* Tool Information */}
          <details className="debug-error-details-section">
            <summary className="debug-error-details-summary">
              <i className="fa fa-chevron-right debug-error-chevron-icon" />
              {I('Tool Information')}
            </summary>
            <div className="debug-error-details-content">
              {errorData.tool_name && (
                <div className="debug-error-detail-row">
                  <p className="debug-error-detail-label">{I('Tool Name')}</p>
                  <div className="debug-error-detail-value">{errorData.tool_name}</div>
                </div>
              )}
              {errorData.tool_type && (
                <div className="debug-error-detail-row">
                  <p className="debug-error-detail-label">{I('Tool Type')}</p>
                  <div className="debug-error-detail-value">{errorData.tool_type}</div>
                </div>
              )}
              {errorData.test_type && (
                <div className="debug-error-detail-row">
                  <p className="debug-error-detail-label">{I('Test Type')}</p>
                  <div className="debug-error-detail-value">{errorData.test_type}</div>
                </div>
              )}
              <div className="debug-error-detail-row">
                <p className="debug-error-detail-label">{I('Timestamp')}</p>
                <div className="debug-error-detail-value">{new Date().toLocaleString()}</div>
              </div>
            </div>
          </details>

          {/* Raw Error Data */}
          <details className="debug-error-details-section">
            <summary className="debug-error-details-summary">
              <i className="fa fa-chevron-right debug-error-chevron-icon" />
              {I('Raw Error Data')}
            </summary>
            <div className="debug-error-details-content">
              <div className="debug-error-detail-row">
                <p className="debug-error-detail-label">{I('Full Response')}</p>
                <div className="debug-error-detail-value">{JSON.stringify(errorData, null, 2)}</div>
              </div>
            </div>
          </details>
        </>
      )}
    </div>
  );
};

export default DebugErrorView;
